/**
 * Content-type registry — one entry per authored content type, keyed by its
 * route segment (`typeKey`). The generic ContentListPage / ContentForm read
 * an entry to render a type without bespoke screens.
 *
 * Metadata fields (used by routing + sidenav):
 *   typeKey       route segment, e.g. "case-studies"
 *   pathSlug      content-type slug in API paths, e.g. "case-study"
 *   blockKey      type-block JSON key in the full-content payload
 *   label         plural display name
 *   singularLabel singular display name ("New {singularLabel}")
 *   icon          MUI icon name
 *   author        whether the type uses a CMS author select
 *   gated         false | "forced" (whitepapers are always gated)
 *
 * Editor fields (added in Batches 2/3):
 *   Fields            type-block field component, rendered by ContentForm
 *   defaultBlock      empty type-block state for a new item
 *   toBlockPayload    form state → request type block (drop empty optionals)
 *   fromBlockResponse FullContentResponse type block → form state
 *   validateBlock     optional (state, context) → error string | null
 *   extraColumns      type-specific EntityTable columns for ContentListPage
 */

import ArticleFields from "components/content/blocks/ArticleFields";
import EventFields from "components/content/blocks/EventFields";
import SocialPostFields from "components/content/blocks/SocialPostFields";
import BlogFields from "components/content/blocks/BlogFields";
import CaseStudyFields from "components/content/blocks/CaseStudyFields";
import TestimonialFields from "components/content/blocks/TestimonialFields";
import WhitepaperFields from "components/content/blocks/WhitepaperFields";
import {
  toRFC3339,
  toDateTimeLocal,
  toDateInput,
  dateToRFC3339,
} from "components/content/contentUtils";

export const CONTENT_TYPES = {
  articles: {
    typeKey: "articles",
    pathSlug: "article",
    blockKey: "article",
    label: "Articles",
    singularLabel: "Article",
    icon: "article",
    author: true,
    gated: false,
    Fields: ArticleFields,
    defaultBlock: {
      is_featured: false,
      source_url: "",
    },
    toBlockPayload: (b) => {
      const block = { is_featured: !!b.is_featured };
      if (b.source_url) block.source_url = b.source_url;
      return block;
    },
    fromBlockResponse: (r) => ({
      is_featured: !!r.is_featured,
      source_url: r.source_url || "",
    }),
    extraColumns: [],
  },
  events: {
    typeKey: "events",
    pathSlug: "event",
    blockKey: "event",
    label: "Events",
    singularLabel: "Event",
    icon: "event",
    author: true,
    gated: false,
    Fields: EventFields,
    defaultBlock: {
      is_featured: false,
      event_mode: "",
      organizer_type: "",
      organizer_name: "",
      organizer_url: "",
      event_start_at: "",
      event_end_at: "",
      timezone: "",
      venue_name: "",
      venue_address: "",
      online_url: "",
      registration_type: "none",
      registration_url: "",
      registration_form_id: null,
      registration_deadline: "",
      is_invite_only: false,
      is_paid: false,
      fee_amount: "",
      fee_currency: "",
      capacity: "",
      recording_url: "",
      speakers: "",
      agenda: "",
    },
    toBlockPayload: (b) => {
      const block = {
        is_featured: !!b.is_featured,
        event_mode: b.event_mode,
        organizer_type: b.organizer_type,
        registration_type: b.registration_type || "none",
        is_invite_only: !!b.is_invite_only,
        is_paid: !!b.is_paid,
      };
      const start = toRFC3339(b.event_start_at);
      const end = toRFC3339(b.event_end_at);
      const deadline = toRFC3339(b.registration_deadline);
      if (start) block.event_start_at = start;
      if (end) block.event_end_at = end;
      if (deadline) block.registration_deadline = deadline;
      if (b.organizer_name) block.organizer_name = b.organizer_name;
      if (b.organizer_url) block.organizer_url = b.organizer_url;
      if (b.timezone) block.timezone = b.timezone;
      if (b.venue_name) block.venue_name = b.venue_name;
      if (b.venue_address) block.venue_address = b.venue_address;
      if (b.online_url) block.online_url = b.online_url;
      if (b.registration_url) block.registration_url = b.registration_url;
      if (b.registration_form_id) block.registration_form_id = b.registration_form_id;
      if (b.recording_url) block.recording_url = b.recording_url;
      if (b.fee_currency) block.fee_currency = b.fee_currency;
      if (b.speakers) block.speakers = b.speakers;
      if (b.agenda) block.agenda = b.agenda;
      const fee = parseFloat(b.fee_amount);
      if (!Number.isNaN(fee)) block.fee_amount = fee;
      const capacity = parseInt(b.capacity, 10);
      if (!Number.isNaN(capacity)) block.capacity = capacity;
      return block;
    },
    fromBlockResponse: (r) => ({
      is_featured: !!r.is_featured,
      event_mode: r.event_mode || "",
      organizer_type: r.organizer_type || "",
      organizer_name: r.organizer_name || "",
      organizer_url: r.organizer_url || "",
      event_start_at: toDateTimeLocal(r.event_start_at),
      event_end_at: toDateTimeLocal(r.event_end_at),
      timezone: r.timezone || "",
      venue_name: r.venue_name || "",
      venue_address: r.venue_address || "",
      online_url: r.online_url || "",
      registration_type: r.registration_type || "none",
      registration_url: r.registration_url || "",
      registration_form_id: r.registration_form_id || null,
      registration_deadline: toDateTimeLocal(r.registration_deadline),
      is_invite_only: !!r.is_invite_only,
      is_paid: !!r.is_paid,
      fee_amount: r.fee_amount != null ? String(r.fee_amount) : "",
      fee_currency: r.fee_currency || "",
      capacity: r.capacity != null ? String(r.capacity) : "",
      recording_url: r.recording_url || "",
      speakers: r.speakers || "",
      agenda: r.agenda || "",
    }),
    validateBlock: (b) => {
      if (!b.event_mode) return "Event mode is required.";
      if (!b.organizer_type) return "Organizer type is required.";
      if (!b.event_start_at) return "Event start date is required.";
      if (!b.event_end_at) return "Event end date is required.";
      if (new Date(b.event_end_at) < new Date(b.event_start_at)) {
        return "Event end must be on or after the event start.";
      }
      if (["offline", "hybrid"].includes(b.event_mode) && !b.venue_name) {
        return "Venue name is required for offline and hybrid events.";
      }
      if (["online", "hybrid"].includes(b.event_mode) && !b.online_url) {
        return "Online URL is required for online and hybrid events.";
      }
      if (b.registration_type === "external_link" && !b.registration_url) {
        return "Registration URL is required for external-link registration.";
      }
      if (b.registration_type === "form" && !b.registration_form_id) {
        return "A registration form is required for form-based registration.";
      }
      return null;
    },
    extraColumns: [],
  },
  "social-posts": {
    typeKey: "social-posts",
    pathSlug: "social-post",
    blockKey: "social_post",
    label: "Social Posts",
    singularLabel: "Social Post",
    icon: "share",
    author: false,
    gated: false,
    Fields: SocialPostFields,
    defaultBlock: {
      platform: "",
      author_name: "",
      author_handle: "",
      author_avatar_url: "",
      external_url: "",
      is_featured: false,
    },
    toBlockPayload: (b) => {
      const block = { platform: b.platform, is_featured: !!b.is_featured };
      if (b.author_name) block.author_name = b.author_name;
      if (b.author_handle) block.author_handle = b.author_handle;
      if (b.author_avatar_url) block.author_avatar_url = b.author_avatar_url;
      if (b.external_url) block.external_url = b.external_url;
      return block;
    },
    fromBlockResponse: (r) => ({
      platform: r.platform || "",
      author_name: r.author_name || "",
      author_handle: r.author_handle || "",
      author_avatar_url: r.author_avatar_url || "",
      external_url: r.external_url || "",
      is_featured: !!r.is_featured,
    }),
    validateBlock: (b) => (b.platform ? null : "Platform is required."),
    extraColumns: [],
  },
  blog: {
    typeKey: "blog",
    pathSlug: "blog",
    blockKey: "blog",
    label: "Blog",
    singularLabel: "Blog Post",
    icon: "rss_feed",
    author: true,
    gated: false,
    Fields: BlogFields,
    defaultBlock: {
      estimated_reading_time: "",
      external_canonical_url: "",
      is_featured: false,
      allow_comments: false,
    },
    toBlockPayload: (b) => {
      const block = { is_featured: !!b.is_featured, allow_comments: !!b.allow_comments };
      const minutes = parseInt(b.estimated_reading_time, 10);
      if (!Number.isNaN(minutes)) block.estimated_reading_time = minutes;
      if (b.external_canonical_url) block.external_canonical_url = b.external_canonical_url;
      return block;
    },
    fromBlockResponse: (r) => ({
      estimated_reading_time:
        r.estimated_reading_time != null ? String(r.estimated_reading_time) : "",
      external_canonical_url: r.external_canonical_url || "",
      is_featured: !!r.is_featured,
      allow_comments: r.allow_comments ?? true,
    }),
    extraColumns: [],
  },
  "case-studies": {
    typeKey: "case-studies",
    pathSlug: "case-study",
    blockKey: "case_study",
    label: "Case Studies",
    singularLabel: "Case Study",
    icon: "work",
    author: true,
    gated: false,
    Fields: CaseStudyFields,
    defaultBlock: {
      client_name: "",
      client_company: "",
      industry: "",
      project_duration: "",
      project_start_date: "",
      project_end_date: "",
      challenge: "",
      solution: "",
      results: "",
      metrics: [],
      testimonial_quote: "",
      client_logo: null,
    },
    toBlockPayload: (b) => {
      const block = {
        client_name: b.client_name,
        challenge: b.challenge,
        solution: b.solution,
        results: b.results,
      };
      if (b.client_company) block.client_company = b.client_company;
      if (b.industry) block.industry = b.industry;
      if (b.project_duration) block.project_duration = b.project_duration;
      const start = dateToRFC3339(b.project_start_date);
      const end = dateToRFC3339(b.project_end_date);
      if (start) block.project_start_date = start;
      if (end) block.project_end_date = end;
      if (b.testimonial_quote) block.testimonial_quote = b.testimonial_quote;
      if (b.client_logo) block.client_logo = b.client_logo;
      const metrics = (b.metrics || []).filter((m) => m.key.trim());
      if (metrics.length) {
        block.metrics_achieved = metrics.reduce((acc, m) => {
          acc[m.key.trim()] = m.value;
          return acc;
        }, {});
      }
      return block;
    },
    fromBlockResponse: (r) => ({
      client_name: r.client_name || "",
      client_company: r.client_company || "",
      industry: r.industry || "",
      project_duration: r.project_duration || "",
      project_start_date: toDateInput(r.project_start_date),
      project_end_date: toDateInput(r.project_end_date),
      challenge: r.challenge || "",
      solution: r.solution || "",
      results: r.results || "",
      metrics: Object.entries(r.metrics_achieved || {}).map(([key, value]) => ({
        key,
        value: String(value),
      })),
      testimonial_quote: r.testimonial_quote || "",
      client_logo: r.client_logo || null,
    }),
    validateBlock: (b) => {
      if (!b.client_name.trim()) return "Client name is required.";
      if (!b.challenge.trim()) return "The Challenge section is required.";
      if (!b.solution.trim()) return "The Solution section is required.";
      if (!b.results.trim()) return "The Results section is required.";
      return null;
    },
    extraColumns: [],
  },
  testimonials: {
    typeKey: "testimonials",
    pathSlug: "testimonial",
    blockKey: "testimonial",
    label: "Testimonials",
    singularLabel: "Testimonial",
    icon: "format_quote",
    author: true,
    gated: false,
    Fields: TestimonialFields,
    defaultBlock: {
      client_name: "",
      client_company: "",
      client_position: "",
      client_location: "",
      rating: 0,
      project_type: "",
      project_date: "",
      client_photo: null,
    },
    toBlockPayload: (b) => {
      const block = { client_name: b.client_name, client_company: b.client_company };
      if (b.client_position) block.client_position = b.client_position;
      if (b.client_location) block.client_location = b.client_location;
      if (b.rating) block.rating = b.rating;
      if (b.project_type) block.project_type = b.project_type;
      const date = dateToRFC3339(b.project_date);
      if (date) block.project_date = date;
      if (b.client_photo) block.client_photo = b.client_photo;
      return block;
    },
    fromBlockResponse: (r) => ({
      client_name: r.client_name || "",
      client_company: r.client_company || "",
      client_position: r.client_position || "",
      client_location: r.client_location || "",
      rating: r.rating || 0,
      project_type: r.project_type || "",
      project_date: toDateInput(r.project_date),
      client_photo: r.client_photo || null,
    }),
    validateBlock: (b) => {
      if (!b.client_name.trim()) return "Client name is required.";
      if (!b.client_company.trim()) return "Client company is required.";
      if (b.rating && (b.rating < 1 || b.rating > 5)) {
        return "Rating must be between 1 and 5.";
      }
      return null;
    },
    extraColumns: [],
  },
  whitepapers: {
    typeKey: "whitepapers",
    pathSlug: "whitepaper",
    blockKey: "whitepaper",
    label: "Whitepapers",
    singularLabel: "Whitepaper",
    icon: "description",
    author: true,
    gated: "forced",
    Fields: WhitepaperFields,
    defaultBlock: {
      download_file: null,
      industry: "",
      target_audience: "",
      author_credentials: "",
    },
    toBlockPayload: (b) => {
      const block = {};
      if (b.download_file) block.download_file = b.download_file;
      if (b.industry) block.industry = b.industry;
      if (b.target_audience) block.target_audience = b.target_audience;
      if (b.author_credentials) block.author_credentials = b.author_credentials;
      return block;
    },
    fromBlockResponse: (r) => ({
      download_file: r.download_file || null,
      industry: r.industry || "",
      target_audience: r.target_audience || "",
      author_credentials: r.author_credentials || "",
    }),
    validateBlock: (b) => (b.download_file ? null : "A download file is required for whitepapers."),
    extraColumns: [],
  },
};

// Insertion order = sidenav order.
export const CONTENT_TYPE_KEYS = Object.keys(CONTENT_TYPES);

export const getContentType = (typeKey) => CONTENT_TYPES[typeKey];
